import { useState } from 'react';
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Modal } from '@liminal/web-ui/overlays/Modal';
import { ConfirmationDialog } from '@liminal/web-ui/overlays/ConfirmationDialog';
import { Input } from '@liminal/web-ui/forms/Input';
import { Textarea } from '@liminal/web-ui/forms/Textarea';
import { FormErrorBanner } from '@liminal/web-ui/forms/FormErrorBanner';
import { useCreatePushAnnouncement } from '@/hooks/usePushAnnouncements';
import type { AdminPushAnnouncement } from '@/api/types';
import { PushAnnouncementPreview } from './PushAnnouncementPreview';
import { AudienceSelector, type AudienceSelection } from './AudienceSelector';
import { useOpeningKey } from '@/hooks/useOpeningKey';
import { getErrorMessage } from '@/errors/messages';

const TITLE_MAX = 255;
const MESSAGE_MAX = 1000;
const URL_MAX = 500;

// Form on the left, live preview on the right; they stack on a narrow window
// so the preview never squeezes the fields into unusability.
const Layout = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  alignItems: 'flex-start',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const PreviewColumn = styled(Box)(({ theme }) => ({
  width: 240,
  flexShrink: 0,
  position: 'sticky',
  top: 0,
  [theme.breakpoints.down('md')]: {
    width: '100%',
    position: 'static',
  },
}));

function toLocalDatetime(isoString: string): string {
  const d = new Date(isoString);
  // Format: YYYY-MM-DDThh:mm
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface PushAnnouncementFormDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Seed the form from an already-sent push. Copies the copy — title, message,
   * deep link — and nothing else: the send time resets to "now", because a
   * time lifted from a past send is already in the past.
   */
  copyFrom?: AdminPushAnnouncement;
}

export function PushAnnouncementFormDialog(props: PushAnnouncementFormDialogProps) {
  // The form's state belongs to one opening of the dialog, for one source.
  const openingKey = useOpeningKey(props.open, props.copyFrom);
  return <PushAnnouncementForm key={openingKey} {...props} />;
}

function PushAnnouncementForm({ open, onClose, copyFrom }: PushAnnouncementFormDialogProps) {
  const createMutation = useCreatePushAnnouncement();

  const [title, setTitle] = useState(copyFrom?.title ?? '');
  const [message, setMessage] = useState(copyFrom?.message ?? '');
  const [url, setUrl] = useState(copyFrom?.url ?? '');
  const [mode, setMode] = useState<'now' | 'schedule'>('now');
  // The picker starts an hour out, so "schedule" opens on a valid future time.
  const [sendAt, setSendAt] = useState(() =>
    toLocalDatetime(new Date(Date.now() + 60 * 60 * 1000).toISOString()),
  );
  const [error, setError] = useState('');
  const [confirmSendNow, setConfirmSendNow] = useState(false);
  const [audience, setAudience] = useState<AudienceSelection>({ valid: true, summary: 'all users' });

  // The datetime-local input holds a partial value while it is being typed,
  // which parses to Invalid Date — the banner stamps "now" until it is whole.
  const scheduledAt = new Date(sendAt);
  const previewScheduledFor =
    mode === 'schedule' && !isNaN(scheduledAt.getTime()) ? scheduledAt : null;

  const submit = async () => {
    const trimmedUrl = url.trim();
    try {
      await createMutation.mutateAsync({
        title: title.trim(),
        message: message.trim(),
        ...(trimmedUrl && { url: trimmedUrl }),
        ...(mode === 'schedule' && { sendAt: new Date(sendAt).toISOString() }),
        ...(audience.audience && { audience: audience.audience }),
      });
      setConfirmSendNow(false);
      onClose();
    } catch (e) {
      // Rendered by whichever surface is open: the confirm while it shows,
      // the form banner once the confirm is dismissed.
      setError(getErrorMessage(e));
    }
  };

  const handleSubmit = () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!message.trim()) {
      setError('Message is required');
      return;
    }
    const trimmedUrl = url.trim();
    // Mirrors the backend / mobile internal-app-path gate.
    if (
      trimmedUrl &&
      (!trimmedUrl.startsWith('/') || trimmedUrl.startsWith('//') || trimmedUrl.includes('://'))
    ) {
      setError('Deep link must be an internal app path starting with / (e.g. /savings)');
      return;
    }
    if (mode === 'schedule') {
      if (!sendAt) {
        setError('Send time is required');
        return;
      }
      if (new Date(sendAt) <= new Date()) {
        setError('Send time must be in the future');
        return;
      }
    }
    if (!audience.valid) {
      setError(audience.error ?? 'Select an audience');
      return;
    }
    setError('');

    // Was a `window.confirm`. Sending to every user deserves a confirm that
    // can carry the copy, show the send in flight, and surface a failure.
    if (mode === 'now') {
      setConfirmSendNow(true);
      return;
    }
    void submit();
  };

  return (
    <>
      <Modal
        open={open}
        title={copyFrom ? 'Copy Push Announcement' : 'Send Push Announcement'}
        onClose={onClose}
        width="lg"
        actions={
          <>
            <Button variant="link" onClick={onClose} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={createMutation.isPending}>
              {mode === 'now' ? 'Send Now' : 'Schedule'}
            </Button>
          </>
        }
      >
        <Layout sx={{ pt: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sends a push notification to the selected audience. Users without a
            registered device or who disabled push notifications are skipped
            automatically.
          </Typography>
          <AudienceSelector
            key={String(open)}
            onChange={setAudience}
            disabled={createMutation.isPending}
          />
          <Input
            label="Title"
            value={title}
            onChange={setTitle}
            required
            size="sm"
            maxLength={TITLE_MAX}
            helper={`${title.length}/${TITLE_MAX}`}
          />
          <Textarea
            label="Message"
            value={message}
            onChange={setMessage}
            required
            rows={3}
            maxLength={MESSAGE_MAX}
            helper={`${message.length}/${MESSAGE_MAX}`}
          />
          <Input
            label="Deep Link (optional)"
            value={url}
            onChange={setUrl}
            size="sm"
            maxLength={URL_MAX}
            placeholder="e.g. /savings"
            helper="In-app screen opened when the user taps the notification"
          />
          <RadioGroup
            row
            value={mode}
            onChange={(e) => setMode(e.target.value as 'now' | 'schedule')}
            sx={{ mb: 1 }}
          >
            <FormControlLabel value="now" control={<Radio size="small" />} label="Send now" />
            <FormControlLabel value="schedule" control={<Radio size="small" />} label="Schedule" />
          </RadioGroup>
          {mode === 'schedule' && (
            <Box sx={{ maxWidth: 280 }}>
              <Input
                label="Send At"
                type="datetime-local"
                size="sm"
                value={sendAt}
                onChange={setSendAt}
                required
              />
            </Box>
          )}
          <Box sx={{ mt: 1 }}>
            <FormErrorBanner error={confirmSendNow ? undefined : error || undefined} />
          </Box>
          </Box>

          <PreviewColumn>
            <PushAnnouncementPreview
              title={title}
              message={message}
              url={url}
              scheduledFor={previewScheduledFor}
            />
          </PreviewColumn>
        </Layout>
      </Modal>

      <ConfirmationDialog
        open={confirmSendNow}
        title={`Send to ${audience.summary} now?`}
        message="This goes immediately to the selected audience's registered devices. There is no unsend."
        confirmLabel="Send now"
        destructive
        loading={createMutation.isPending}
        onConfirm={() => void submit()}
        onCancel={() => setConfirmSendNow(false)}
        data-testid="confirm-send-push-now"
      >
        <FormErrorBanner error={error || undefined} />
      </ConfirmationDialog>
    </>
  );
}
